import { captureEvent } from "@Light/app/analytics";
import { captureException, captureMessage } from "@sentry/react";
import {
  ExpressCheckoutElement,
  ExpressCheckoutElementProps,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import type { ExpressPaymentMethod } from "./PaymentOptions";

const DEFAULT_ERROR =
  "Something went wrong while adding your payment method. Please try again.";

export type StripeExpressCheckoutButtonsProps = {
  /**
   * Renders only this method's button. Stripe can't change which wallets an
   * element offers after it mounts, so switching this must remount the element.
   */
  paymentMethod?: ExpressPaymentMethod;
  onReady?: ExpressCheckoutElementProps["onReady"];
  /**
   * Called when the express checkout button is clicked. If provided, must call
   * `ev.resolve()` or `ev.reject()` within 1 second (does not resolve by default).
   */
  onClick?: ExpressCheckoutElementProps["onClick"];
  onCancel?: ExpressCheckoutElementProps["onCancel"];
  onLoadError?: ExpressCheckoutElementProps["onLoadError"];
  /**
   * Called once the wallet setup is confirmed, with the resulting Stripe
   * payment method id. Should be async if it triggers follow-up behavior (such
   * as account status updates) and throw if those fail.
   */
  onPaymentMethod: (paymentMethodId: string) => void | Promise<void>;
};

export const StripeExpressCheckoutButtons: React.FC<
  StripeExpressCheckoutButtonsProps
> = ({
  paymentMethod: only,
  onReady,
  onClick,
  onCancel,
  onLoadError,
  onPaymentMethod,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string>();

  const onConfirm: ExpressCheckoutElementProps["onConfirm"] = async (event) => {
    setError(undefined);

    // Type-checking only, should never happen; onConfirm is only called after
    // the checkout elements rendered, which doesn't happen without these.
    if (!stripe || !elements) {
      captureMessage("Express checkout confirmed before Stripe was ready");
      event.paymentFailed();
      setError(DEFAULT_ERROR);
      return;
    }

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (stripeError) {
        captureMessage("Stripe express checkout error", {
          extra: { error: stripeError.message },
        });
        setError(stripeError.message ?? DEFAULT_ERROR);
        return;
      }

      if (!setupIntent?.payment_method) {
        captureMessage("No payment method returned from express checkout");
        setError(DEFAULT_ERROR);
        return;
      }

      await onPaymentMethod(setupIntent.payment_method as string);
    } catch (err) {
      captureException(err);
      setError(DEFAULT_ERROR);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error && <div className="text-sm text-error-primary">{error}</div>}
      <ExpressCheckoutElement
        options={{
          buttonHeight: 46, // lg button
          buttonType: {
            applePay: "plain",
            googlePay: "plain",
          },
          paymentMethods: {
            applePay: !only || only === "apple_pay" ? "auto" : "never",
            googlePay: !only || only === "google_pay" ? "auto" : "never",
            link: "never",
            paypal: "never",
            klarna: "never",
            amazonPay: "never",
          },
          billingAddressRequired: true,
          layout: { maxColumns: 1, overflow: "never" },
        }}
        onReady={(ev) => {
          onReady?.(ev);
          const applePay = !!ev.availablePaymentMethods?.applePay;
          const googlePay = !!ev.availablePaymentMethods?.googlePay;

          if (!applePay && !googlePay) {
            captureEvent("express_checkout_no_payment_methods");
            captureMessage(
              "Express checkout could not render any payment methods",
              "info",
            );
            return;
          }

          captureEvent("express_checkout_rendered", {
            apple_pay: applePay,
            google_pay: googlePay,
          });
        }}
        onClick={(event) => {
          onClick?.(event);
          captureEvent("express_checkout_button_clicked", {
            payment_method: event.expressPaymentType,
          });
        }}
        onLoadError={(event) => {
          onLoadError?.(event);
          captureMessage("Express checkout failed to load", {
            level: "info",
            extra: { error: event.error?.message },
          });
        }}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  );
};
